import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, FormGroup, Input, PageHeader } from "@cofano/cck";
import Grid from "@mui/material/Grid2";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";
import { FormArrayOnlyRows } from "components/cck/FormArray";
import NumberInput from "components/cck/NumberInput";
import useRequest from "components/hooks/useRequest";

import { Language } from "imports/Utils";

import WaterLevelSelect from "view/waterlevels/components/selects/WaterLevelSelect";

import { waterlevelStaffelSchema, WaterlevelStaffelSchemaInput, WaterlevelStaffelSchemaOutput } from "./utils/staffel.schema";

import { StaffelInputType } from "__generated__/enums";
import { GetStaffelDocument } from "__generated__/view/staffels/graphql/staffel.queries.generated";
import { SaveStaffelDocument } from "__generated__/view/staffels/graphql/staffel.mutations.generated";

const WaterLevelStaffelCreateEdit = () => {

	const [ measuringPoints ] = useRequest({ defaultResponse: [], url: "waterlevels/measuring-points" });

	const navigate = useNavigate();
	const { id } = useParams();

	const [ saveWaterLevelStaffel, { loading: mutationLoading } ] = useMutation(SaveStaffelDocument);
	const [ getStaffel, { loading: queryLoading } ] = useLazyQuery(GetStaffelDocument);

	const { control, handleSubmit, reset } = useForm<WaterlevelStaffelSchemaInput, unknown, WaterlevelStaffelSchemaOutput>({
		resolver: zodResolver(waterlevelStaffelSchema),
		defaultValues: {
			id: null,
			description: "",
			staffelItems: [],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "staffelItems",
	});

	const save = (data: WaterlevelStaffelSchemaOutput) => {
		saveWaterLevelStaffel({
			variables: {
				input: {
					type: StaffelInputType.WaterLevel,
					waterLevelStaffel: {
						id: data.id,
						description: data.description,
						waterlevelMeasuringPoint: data.waterlevelMeasuringPoint,
						staffelItems: data.staffelItems.map(it => ({
							type: StaffelInputType.WaterLevel,
							waterLevelStaffelItem: {
								id: it.id ?? 0,
								startWaterLevelCm: it.startWaterLevelCm,
								endWaterLevelCm: it.endWaterLevelCm,
								surcharge: it.surcharge,
							},
						})),
					},
				},
			},
		}).then(({ data }) => {
			navigate(`/view/staffels/${data?.saveStaffel?.id}/details/waterlevel`);
		});
	};

	useEffect(() => {
		if (id) {
			getStaffel({
				variables: {
					id: Number(id),
				} })
				.then((result) => {
					if (result.data?.staffel) {
						reset({
							id: result.data.staffel.id,
							description: result.data.staffel.description ?? "",
							waterlevelMeasuringPoint: result.data.staffel.__typename === "WaterLevelStaffel" ? result.data.staffel.waterlevelMeasuringPoint : null,
							staffelItems: result.data.staffel.staffelItems?.map(it => ({
								id: it.id,
								startWaterLevelCm: it.__typename === "WaterLevelStaffelItem" ? it.startWaterLevelCm : null,
								endWaterLevelCm: it.__typename === "WaterLevelStaffelItem" ? it.endWaterLevelCm : null,
								surcharge: it.__typename === "WaterLevelStaffelItem" ? it.surcharge : null,
							})) ?? [],
						});
					}
				});
		}
	}, [ id ]);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("WATERLEVEL")}
					<Helmet title={intl.formatMessage({ id: "WATERLEVEL" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate(id ? `/view/staffels/${id}/details/waterlevel` : "/view/staffels")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={queryLoading || mutationLoading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={6}>
					<Card>
						<FormGroup
							label={intl.formatMessage({ id: "DESCRIPTION" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="description"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "LOCATION" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="waterlevelMeasuringPoint"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<WaterLevelSelect
											value={measuringPoints.find((it: { id: number }) => it.id === field.value)}
											onChange={option => field.onChange(option?.id ?? null)} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormArrayOnlyRows
							array={fields}
							fields={[ {
								title: intl.formatMessage({ id: "WATERLEVEL_START_HEIGHT" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.startWaterLevelCm`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} decimalScale={0} addonAfter="cm" />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "WATERLEVEL_END_HEIGHT" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.endWaterLevelCm`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} decimalScale={0} addonAfter="cm" />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "GASOLINE_STAFFEL_SURCHARGE" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.surcharge`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							} ]}
							onAdd={() => {
								append({
									id: 0,
									startWaterLevelCm: 0,
									endWaterLevelCm: 0,
									surcharge: 0,
								});
							}}
							onDelete={remove} />
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default WaterLevelStaffelCreateEdit;
